import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, User, MessageSquare, FileText, Clock, ChevronRight, Phone } from 'lucide-react';
import { RealtimeKitProvider, useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';

interface UniversalVideoSessionProps {
  roomId?: string;
  requestId?: string;
  agentName?: string;
  participantType?: 'host' | 'customer';
  customerName?: string;
  sessionDetails?: {
    title: string;
    category: string;
    urgency: string;
    description?: string;
  };
  isDemo?: boolean;
  hideSidebar?: boolean;
}

// Inner component that uses RealtimeKit hooks
const UniversalMeetingComponent: React.FC<{
  authToken: string;
  roomId: string;
  participantName: string;
  participantType: string;
  requestId: string;
  customerEmail: string;
  agentName: string;
  sessionDetails: {
    title: string;
    category: string;
    urgency: string;
    description?: string;
  };
  isDemo?: boolean;
  hideSidebar?: boolean;
}> = ({ authToken, roomId, participantName, participantType, requestId, customerEmail, agentName, sessionDetails, isDemo = false, hideSidebar = false }) => {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showCustomerInfo, setShowCustomerInfo] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [sessionNotes, setSessionNotes] = useState('');
  const [customerHistory, setCustomerHistory] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!authToken || isInitialized) return;
      
      try {
        console.log('[Universal] Initializing RealtimeKit meeting');
        console.log('[Universal] AuthToken:', authToken);
        await initMeeting({
          authToken: authToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
        setIsInitialized(true);
        console.log('[Universal] Meeting initialized successfully');
      } catch (error) {
        console.error('[Universal] Failed to initialize meeting:', error);
      }
    };

    init();
  }, [authToken, initMeeting, isInitialized]);

  // Timer for session time
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Track demo session end when page unloads (browser close, navigation away)
  useEffect(() => {
    if (!isDemo) return;
    
    // Check if this is an admin session and skip tracking
    const urlParams = new URLSearchParams(window.location.search);
    const isAdminSession = urlParams.get('isAdmin') === 'true';
    if (isAdminSession) {
      console.log('[UniversalVideoSession] Admin session detected - skipping duration tracking');
      return;
    }

    const handleBeforeUnload = async () => {
      if (sessionTime > 0) {
        const durationMinutes = Math.ceil(sessionTime / 60);
        const pathParts = window.location.pathname.split('/');
        const sessionId = pathParts[pathParts.length - 1];
        
        if (sessionId && sessionId.startsWith('demo_')) {
          // Use sendBeacon for reliable tracking during page unload
          const blob = new Blob([JSON.stringify({ 
            durationMinutes,
            participantType: participantType, // Pass participant type to backend
            terminateRoom: participantType === 'host' // Only hosts can terminate the room
          })], {
            type: 'application/json'
          });
          navigator.sendBeacon(
            `https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/end-session/${sessionId}`,
            blob
          );
          console.log(`[UniversalVideoSession] Demo session ${sessionId} tracked via sendBeacon by ${participantType}: ${durationMinutes} minutes`);
        }
      }
    };

    // Add event listeners for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [isDemo, sessionTime]);

  // Format time
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch customer history
  const fetchCustomerHistory = async (customerEmail: string) => {
    if (!customerEmail || loadingHistory) return;
    
    setLoadingHistory(true);
    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/customers/${encodeURIComponent(customerEmail)}/history?org_id=org_demo_123456789`);
      if (response.ok) {
        const data = await response.json();
        setCustomerHistory(data);
      }
    } catch (error) {
      console.error('Error fetching customer history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };


  // Save session notes
  const saveSessionNotes = async () => {
    if (!sessionNotes.trim() || participantType !== 'host') return;
    
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/session-notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_email: customerEmail,
          customer_name: participantName,
          session_id: requestId,
          session_type: 'universal',
          agent_name: 'Universal Agent',
          notes: sessionNotes,
          tags: [],
          session_duration_minutes: Math.floor(sessionTime / 60),
          organization_id: 'org_demo_123456789'
        })
      });
      
      if (response.ok) {
        console.log('Session notes saved successfully');
        setSessionNotes(''); // Clear notes after saving
      }
    } catch (error) {
      console.error('Error saving session notes:', error);
    }
  };

  // Auto-save notes when session ends
  const handleEndCallWithNotes = async () => {
    if (sessionNotes.trim() && participantType === 'host') {
      await saveSessionNotes();
    }
    
    // For demo sessions, track the actual session duration (skip for admin)
    if (isDemo && sessionTime > 0) {
      try {
        // Check if this is an admin session and skip tracking
        const urlParams = new URLSearchParams(window.location.search);
        const isAdminSession = urlParams.get('isAdmin') === 'true';
        if (isAdminSession) {
          console.log(`[UniversalVideoSession] Admin session - skipping duration tracking`);
          return;
        }
        
        const durationMinutes = Math.ceil(sessionTime / 60); // Convert seconds to minutes, round up
        console.log(`[UniversalVideoSession] Demo session ending, tracking ${durationMinutes} minutes`);
        
        // Extract sessionId from URL path (e.g., /public/demo/host/demo_123456789_abcdef)
        const pathParts = window.location.pathname.split('/');
        const sessionId = pathParts[pathParts.length - 1];
        
        if (sessionId && sessionId.startsWith('demo_')) {
          await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/end-session/${sessionId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              durationMinutes: durationMinutes,
              participantType: participantType, // Pass participant type to backend
              terminateRoom: participantType === 'host' // Only hosts can terminate the room
            })
          });
          console.log(`[UniversalVideoSession] Demo session ${sessionId} ended by ${participantType}, tracked ${durationMinutes} minutes`);
        }
      } catch (error) {
        console.error('[UniversalVideoSession] Error tracking demo session end:', error);
      }
    }
    
    // Get the current org ID from the URL
    const currentPath = window.location.pathname;
    const orgMatch = currentPath.match(/^\/([a-zA-Z0-9_]+)/);
    const orgPrefix = orgMatch && orgMatch[1].startsWith('org_') ? `/${orgMatch[1]}` : '';
    
    console.log(`[UniversalVideoSession] ${participantType} ending call`);
    if (participantType === 'customer') {
      // Redirect to mobile-optimized session ended page with parameters
      const sessionEndedUrl = `${orgPrefix}/templates/universal/session-ended?customerName=${encodeURIComponent(participantName)}&agentName=${encodeURIComponent(agentName)}&companyName=${encodeURIComponent('Universal Support')}&sessionDuration=${formatTime(sessionTime)}`;
      console.log('[UniversalVideoSession] Redirecting customer to session ended page:', sessionEndedUrl);
      window.location.href = sessionEndedUrl;
    } else {
      window.location.href = `${orgPrefix}/templates/universal/dashboard`;
    }
  };

  if (!meeting) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="animate-spin h-16 w-16 mx-auto mb-4" />
          <p>Connecting to video session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Session Header - Different for Host vs Customer */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
{participantType === 'host' ? (
              <>
                <h1 className="text-xl font-semibold text-gray-900">
                  🔧 Universal Session - {sessionDetails.title} (HOST VIEW)
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                  <span>Request: {requestId}</span>
                  <span>•</span>
                  <span className="capitalize">{sessionDetails.urgency} Priority</span>
                  <span>•</span>
                  <span className="capitalize bg-blue-100 px-2 py-1 rounded">HOST</span>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-xl font-semibold text-gray-900">
                  👤 Universal Support Session (CUSTOMER VIEW)
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                  <span className="bg-green-100 px-2 py-1 rounded">Connected to support agent</span>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{participantName}</p>
              <p className="text-xs text-gray-600">
                {participantType === 'host' ? sessionDetails.category : 'Customer'}
              </p>
            </div>
            {participantType === 'host' && (
              <div className="flex items-center text-sm text-gray-600">
                <Clock size={16} className="mr-2" />
                {formatTime(sessionTime)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Video Area */}
        <div className="flex-1 relative bg-black">
          <RtkMeeting 
            meeting={meeting} 
            showSetupScreen={false}
            mode="fill"
          />
          
          {/* Video Controls Overlay - Different for Host vs Customer */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <div className="bg-gray-800 bg-opacity-90 rounded-full px-4 py-2 flex items-center space-x-3">
              {/* Host Controls */}
              {participantType === 'host' && (
                <>
                  {/* Chat Toggle */}
                  <button
                    onClick={() => setShowChat(!showChat)}
                    className={`p-2 rounded-full transition-colors ${
                      showChat 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Chat"
                  >
                    <MessageSquare size={18} />
                  </button>

                  {/* Customer Info Toggle */}
                  <button
                    onClick={() => setShowCustomerInfo(!showCustomerInfo)}
                    className={`p-2 rounded-full transition-colors ${
                      showCustomerInfo 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Customer Info"
                  >
                    <User size={18} />
                  </button>

                  {/* Notes Toggle */}
                  <button
                    onClick={() => setShowNotes(!showNotes)}
                    className={`p-2 rounded-full transition-colors ${
                      showNotes 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Session Notes"
                  >
                    <FileText size={18} />
                  </button>

                  {/* History Toggle */}
                  <button
                    onClick={() => {
                      setShowHistory(!showHistory);
                      if (!showHistory && !customerHistory) {
                        // Fetch history when opening for the first time
                        fetchCustomerHistory(customerEmail);
                      }
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      showHistory 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Customer History"
                  >
                    <Clock size={18} />
                  </button>
                </>
              )}

              {/* End Call (Both Host and Customer) */}
              <button
                onClick={handleEndCallWithNotes}
                className="p-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
                title="End Call"
              >
                <Phone size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {!hideSidebar && (showCustomerInfo || showChat || showNotes || showHistory) && participantType === 'host' && (
          <div className="w-80 bg-white flex flex-col">
            {/* Panel Header */}
            <div className="px-4 py-3 border-b bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  <button
                    onClick={() => setShowCustomerInfo(!showCustomerInfo)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      showCustomerInfo 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <User size={12} className="inline mr-1" />
                    Info
                  </button>
                  <button
                    onClick={() => setShowChat(!showChat)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      showChat 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <MessageSquare size={12} className="inline mr-1" />
                    Chat
                  </button>
                  <button
                    onClick={() => setShowNotes(!showNotes)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      showNotes 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <FileText size={12} className="inline mr-1" />
                    Notes
                  </button>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      showHistory 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <Clock size={12} className="inline mr-1" />
                    History
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowCustomerInfo(false);
                    setShowChat(false);
                    setShowNotes(false);
                    setShowHistory(false);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Collapse side panel"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Customer Info Panel */}
            {showCustomerInfo && (
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-6">
                  {/* Customer Overview */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start space-x-3 mb-3">
                      <User size={24} className="text-blue-600 mt-1" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-900 mb-1">
                          {participantName}
                        </h4>
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            CUSTOMER
                          </span>
                          <span className={`text-sm px-2 py-1 rounded-full ${
                            sessionDetails.urgency === 'high' 
                              ? 'bg-red-100 text-red-800'
                              : sessionDetails.urgency === 'medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {sessionDetails.urgency.toUpperCase()} PRIORITY
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Session Information */}
                  <div className="space-y-3">
                    <h5 className="font-semibold text-gray-900 flex items-center">
                      <FileText size={16} className="mr-2" />
                      Session Information
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-24">Session ID:</span>
                        <span className="text-gray-900">{requestId}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-24">Category:</span>
                        <span className="text-gray-900 capitalize">{sessionDetails.category}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-24">Priority:</span>
                        <span className="text-gray-900 capitalize">{sessionDetails.urgency}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-24">Duration:</span>
                        <span className="text-gray-900">{formatTime(sessionTime)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {sessionDetails.description && (
                    <div className="space-y-3">
                      <h5 className="font-semibold text-gray-900">Description</h5>
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                        {sessionDetails.description}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Chat Panel */}
            {showChat && (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="text-center text-gray-500 text-sm">
                    Chat functionality coming soon
                  </div>
                </div>
              </div>
            )}

            {/* Notes Panel */}
            {showNotes && (
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b bg-green-50">
                  <h4 className="font-semibold text-green-900 mb-2">Session Notes</h4>
                  <p className="text-sm text-green-700">
                    Notes will be automatically saved when the session ends.
                  </p>
                </div>
                <div className="flex-1 p-4">
                  <textarea
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    placeholder="Add your notes about this session here..."
                    className="w-full h-64 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {sessionNotes.length} characters
                    </div>
                    <button
                      onClick={saveSessionNotes}
                      disabled={!sessionNotes.trim()}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Save Now
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* History Panel */}
            {showHistory && (
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b bg-purple-50">
                  <h4 className="font-semibold text-purple-900 mb-2">Customer History</h4>
                  <p className="text-sm text-purple-700">
                    Previous sessions with {participantName}
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="animate-spin h-6 w-6 text-purple-600" />
                      <span className="ml-2 text-sm text-gray-600">Loading history...</span>
                    </div>
                  ) : customerHistory ? (
                    <div className="space-y-4">
                      {/* Customer Summary */}
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-purple-900">{customerHistory.customer.name}</h5>
                          <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full">
                            {customerHistory.customer.total_sessions} sessions
                          </span>
                        </div>
                        <div className="text-xs text-purple-700">
                          <div>Email: {customerHistory.customer.email}</div>
                          <div>First seen: {new Date(customerHistory.customer.first_seen).toLocaleDateString()}</div>
                          <div>Last seen: {new Date(customerHistory.customer.last_seen).toLocaleDateString()}</div>
                        </div>
                      </div>

                      {/* Recent Sessions */}
                      {customerHistory.recent_sessions.length > 0 ? (
                        <div className="space-y-3">
                          <h6 className="font-medium text-gray-900">Recent Sessions</h6>
                          {customerHistory.recent_sessions.map((session: any) => (
                            <div key={session.id} className="bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {session.session_type.charAt(0).toUpperCase() + session.session_type.slice(1)} Session
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(session.session_date).toLocaleDateString()}
                                </span>
                              </div>
                              {session.notes && (
                                <div className="text-sm text-gray-700 bg-white p-2 rounded border">
                                  {session.notes}
                                </div>
                              )}
                              <div className="mt-2 text-xs text-gray-500">
                                Agent: {session.agent_name} • Duration: {session.session_duration_minutes}min
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          No previous sessions found
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      Click to load customer history
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const UniversalVideoSession: React.FC<UniversalVideoSessionProps> = ({
  roomId: initialRoomId = '',
  requestId: initialRequestId = '',
  agentName: initialAgentName = 'Universal Agent',
  participantType: initialParticipantType = 'host',
  customerName: initialCustomerName = 'Customer',
  sessionDetails: initialSessionDetails = {
    title: 'Universal Consultation',
    category: 'consultation',
    urgency: 'medium'
  },
  isDemo = false,
  hideSidebar = false
}) => {
  const { account } = useAuth();
  
  // Parse URL parameters to get actual values
  const urlParams = new URLSearchParams(window.location.search);
  const roomId = initialRoomId || urlParams.get('roomId') || '';
  const requestId = initialRequestId || urlParams.get('requestId') || '';
  const agentName = initialAgentName || urlParams.get('agentName') || 'Universal Agent';
  const customerName = initialCustomerName || urlParams.get('customerName') || 'Customer';
  const customerEmail = urlParams.get('customerEmail') || 'customer@example.com';
  
  // Parse session details from URL parameters
  const sessionDetails = {
    title: urlParams.get('title') || initialSessionDetails.title,
    category: urlParams.get('category') || initialSessionDetails.category,
    urgency: urlParams.get('urgency') || initialSessionDetails.urgency,
    description: urlParams.get('description') || initialSessionDetails.description
  };
  
  // IMPORTANT: Read participantType from URL - this determines the UI
  const participantType = (urlParams.get('participantType') || initialParticipantType) as 'host' | 'customer';
  
  console.log('[UniversalVideoSession] URL params:', {
    roomId,
    requestId,
    participantType,
    customerName,
    agentName
  });
  
  const [authToken, setAuthToken] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);

  // Use participant name based on type
  const participantName = participantType === 'host' 
    ? agentName || `${account?.firstName} ${account?.lastName}`.trim() || 'Universal Agent'
    : customerName;

  // Universal-specific token generation - completely isolated from other templates
  const generateUniversalToken = async (roomId: string, participantName: string, isHost: boolean = false, maxRetries: number = 3) => {
    console.log(`[Universal] Generating universal token for room ${roomId}, participant ${participantName}, host: ${isHost}, demo: ${isDemo}`);
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // For universal template, check if this is an external session ID
        const isExternalSession = roomId.startsWith('ext_sess_');
        
        if (isExternalSession && !isDemo) {
          // For external sessions, use the universal-specific endpoint that creates a real RealtimeKit meeting
          console.log(`[Universal] Detected external session ${roomId}, using universal endpoint`);
          const endpoint = 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/universal/generate-token';
          const body = { 
            externalSessionId: roomId,
            participantName, 
            isHost,
            requestId: requestId
          };
          
          console.log(`[Universal] Using universal endpoint: ${endpoint}`, body);
          
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });

          if (response.ok) {
            const tokenData = await response.json();
            console.log(`[Universal] Universal token generated successfully for ${participantName}${attempt > 1 ? ` (attempt ${attempt})` : ''}`, tokenData);
            return tokenData.token;
          } else {
            console.warn(`[Universal] Universal endpoint failed with ${response.status}, falling back to debug endpoint`);
          }
        }
        
        // Fallback to debug endpoint for demo sessions or when universal endpoint fails
        console.log(`[Universal] Using debug endpoint fallback`);
        const endpoint = 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token';
        const body = { meetingId: roomId, participantName, isHost };
        
        console.log(`[Universal] Using debug endpoint: ${endpoint}`, body);
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          // If it's a 500 error and not the last attempt, retry after a delay
          if (response.status === 500 && attempt < maxRetries) {
            const delay = Math.pow(2, attempt - 1) * 1000; // Exponential backoff: 1s, 2s, 4s
            console.log(`[Universal] Token generation failed (${response.status}), retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
            setRetryAttempt(attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          throw new Error(`Token generation failed: ${response.status}`);
        }

        const tokenData = await response.json();
        console.log(`[Universal] Debug token generated successfully for ${participantName}${attempt > 1 ? ` (attempt ${attempt})` : ''}`, tokenData);
        
        return tokenData.token;
      } catch (error) {
        if (attempt === maxRetries) {
          console.error('[Universal] Token generation error (final attempt):', error);
          throw error;
        }
        
        // For network errors, also retry with delay
        const delay = Math.pow(2, attempt - 1) * 1000;
        console.log(`[Universal] Token generation error, retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`, error);
        setRetryAttempt(attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Initialize session
  useEffect(() => {
    if (!roomId || !participantName) {
      setError('Room ID and participant name are required');
      setIsInitializing(false);
      return;
    }

    const initializeSession = async () => {
      try {
        setError(null);
        console.log(`[Universal] Initializing session for ${participantName} in room ${roomId}`);
        
        // Use universal-specific token generation for all modes
        const token = await generateUniversalToken(roomId, participantName, participantType === 'host');
        setAuthToken(token);
        
        console.log(`[Universal] Universal token generated successfully for ${participantName}`);
        
      } catch (error) {
        console.error('[Universal] Failed to initialize session:', error);
        setError(error instanceof Error ? error.message : 'Failed to initialize session');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeSession();
  }, [roomId, participantName, participantType, isDemo]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (isInitializing || !authToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Loader2 className="animate-spin h-16 w-16 text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {retryAttempt > 0 ? 'Connecting to Video Room...' : 'Loading Session...'}
          </h2>
          <p className="text-gray-600 mb-2">
            {retryAttempt > 0 
              ? `Establishing connection... (attempt ${retryAttempt + 1}/3)`
              : 'Please wait while we prepare your session.'
            }
          </p>
          <p className="text-sm text-gray-500">Room: {roomId}</p>
          <p className="text-sm text-gray-500">Participant: {participantName}</p>
          {retryAttempt > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">
                The video room is being prepared. This usually takes just a moment...
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // The RealtimeKitProvider is not needed here - we initialize the client inside the component
  return (
    <UniversalMeetingComponent
      authToken={authToken}
      roomId={roomId}
      participantName={participantName}
      participantType={participantType}
      requestId={requestId}
      customerEmail={customerEmail}
      agentName={agentName}
      sessionDetails={sessionDetails}
      isDemo={isDemo}
      hideSidebar={hideSidebar}
    />
  );
}; 